/**
 * SteemWorld Content Search API
 *
 * Provides methods for searching posts and comments using the SteemWorld content_search_api.
 */

import { contentSearchApi } from '../client'
import { STEEMWORLD_CONFIG } from '../config'
import type { ContentSearchQueryParams, ContentSearchApiResponse, SearchField } from '../types'

/**
 * Builds common search query parameters
 */
function buildSearchParams(params: ContentSearchQueryParams = {}) {
  const { defaults } = STEEMWORLD_CONFIG

  return {
    limit: params.limit ?? defaults.limit,
    offset: params.offset ?? defaults.offset,
    bodyLength: params.bodyLength ?? defaults.bodyLength,
    observer: params.observer,
    order: params.order ?? 'desc',
    sortBy: params.sortBy ?? 'created',
  }
}

/**
 * Encodes search text for URL path
 */
function encodeSearchText(text: string): string {
  return encodeURIComponent(text.trim())
}

/**
 * Encodes tags for URL path (comma-separated to underscore-separated for some endpoints)
 */
function encodeTags(tags: string): string {
  return encodeURIComponent(tags.trim())
}

/**
 * Search posts by author, tags, and text
 *
 * @param author - Author username to filter by
 * @param tags - Comma-separated tags to filter by
 * @param searchText - Text to search for
 * @param searchIn - Field to search in (title, body, or title,body)
 * @param params - Query parameters
 * @returns Search results
 */
export async function getPostsByAuthorTagsText(
  author: string,
  tags: string,
  searchText: string,
  searchIn: SearchField = 'title,body',
  params: ContentSearchQueryParams = {}
): Promise<ContentSearchApiResponse> {
  const encodedTags = encodeTags(tags)
  const encodedText = encodeSearchText(searchText)
  const encodedSearchIn = encodeURIComponent(searchIn)

  return contentSearchApi.request<ContentSearchApiResponse>(
    `/getPostsByAuthorTagsText/${author}/${encodedTags}/${encodedText}/${encodedSearchIn}`,
    {
      params: buildSearchParams(params),
    }
  )
}

/**
 * Search posts by tags and text (without author filter)
 *
 * @param tags - Comma-separated tags to filter by
 * @param searchText - Text to search for
 * @param searchIn - Field to search in (title, body, or title,body)
 * @param params - Query parameters
 * @returns Search results
 */
export async function getPostsByTagsText(
  tags: string,
  searchText: string,
  searchIn: SearchField = 'title,body',
  params: ContentSearchQueryParams = {}
): Promise<ContentSearchApiResponse> {
  const encodedTags = encodeTags(tags)
  const encodedText = encodeSearchText(searchText)
  const encodedSearchIn = encodeURIComponent(searchIn)

  return contentSearchApi.request<ContentSearchApiResponse>(
    `/getPostsByTagsText/${encodedTags}/${encodedText}/${encodedSearchIn}`,
    {
      params: buildSearchParams(params),
    }
  )
}

/**
 * Search posts by text only (global search)
 *
 * @param searchText - Text to search for
 * @param searchIn - Field to search in (title, body, or title,body)
 * @param params - Query parameters
 * @returns Search results
 */
export async function getPostsByText(
  searchText: string,
  searchIn: SearchField = 'title,body',
  params: ContentSearchQueryParams = {}
): Promise<ContentSearchApiResponse> {
  const encodedText = encodeSearchText(searchText)
  const encodedSearchIn = encodeURIComponent(searchIn)

  return contentSearchApi.request<ContentSearchApiResponse>(
    `/getPostsByText/${encodedText}/${encodedSearchIn}`,
    {
      params: buildSearchParams(params),
    }
  )
}

/**
 * Search posts by author and text
 *
 * @param author - Author username to filter by
 * @param searchText - Text to search for
 * @param searchIn - Field to search in (title, body, or title,body)
 * @param params - Query parameters
 * @returns Search results
 */
export async function getPostsByAuthorText(
  author: string,
  searchText: string,
  searchIn: SearchField = 'title,body',
  params: ContentSearchQueryParams = {}
): Promise<ContentSearchApiResponse> {
  const encodedText = encodeSearchText(searchText)
  const encodedSearchIn = encodeURIComponent(searchIn)

  return contentSearchApi.request<ContentSearchApiResponse>(
    `/getPostsByAuthorText/${author}/${encodedText}/${encodedSearchIn}`,
    {
      params: buildSearchParams(params),
    }
  )
}

/**
 * Search active comments by author, tags, and text
 *
 * @param author - Author username to filter by
 * @param tags - Comma-separated tags to filter by
 * @param searchText - Text to search for
 * @param searchIn - Field to search in (title, body, or title,body)
 * @param params - Query parameters
 * @returns Search results
 */
export async function getActiveCommentsByAuthorTagsText(
  author: string,
  tags: string,
  searchText: string,
  searchIn: SearchField = 'body',
  params: ContentSearchQueryParams = {}
): Promise<ContentSearchApiResponse> {
  const encodedTags = encodeTags(tags)
  const encodedText = encodeSearchText(searchText)
  const encodedSearchIn = encodeURIComponent(searchIn)

  return contentSearchApi.request<ContentSearchApiResponse>(
    `/getActiveCommentsByAuthorTagsText/${author}/${encodedTags}/${encodedText}/${encodedSearchIn}`,
    {
      params: buildSearchParams(params),
    }
  )
}

/**
 * Search posts by tag (single tag)
 *
 * @param tag - Tag to filter by
 * @param params - Query parameters
 * @returns Search results
 */
export async function getPostsByTag(
  tag: string,
  params: ContentSearchQueryParams = {}
): Promise<ContentSearchApiResponse> {
  return contentSearchApi.request<ContentSearchApiResponse>(
    `/getPostsByTag/${encodeURIComponent(tag)}`,
    {
      params: buildSearchParams(params),
    }
  )
}

/**
 * Unified search function that chooses the appropriate API based on parameters
 *
 * @param options - Search options
 * @returns Search results
 */
export async function search(options: {
  query: string
  author?: string
  tags?: string
  searchIn?: SearchField
  params?: ContentSearchQueryParams
}): Promise<ContentSearchApiResponse> {
  const { query, author, tags, searchIn = 'title,body', params = {} } = options

  // If no query text, search by tag only
  if (!query.trim() && tags) {
    const firstTag = tags.split(',')[0]
    if (firstTag) {
      return getPostsByTag(firstTag, params)
    }
  }

  // Choose the appropriate search endpoint based on provided filters
  if (author && tags) {
    return getPostsByAuthorTagsText(author, tags, query, searchIn, params)
  }

  if (author) {
    return getPostsByAuthorText(author, query, searchIn, params)
  }

  if (tags) {
    return getPostsByTagsText(tags, query, searchIn, params)
  }

  // Default: search by text only
  return getPostsByText(query, searchIn, params)
}

/**
 * Search API namespace
 */
export const contentSearch = {
  getPostsByAuthorTagsText,
  getPostsByTagsText,
  getPostsByText,
  getPostsByAuthorText,
  getActiveCommentsByAuthorTagsText,
  getPostsByTag,
  search,
}
